import * as React from "react";
import { createTheme, ThemeProvider } from "@mui/material";
import { useLocation } from "react-router-dom";
import { sectionFor } from "./theme/section";

/**
 * MUI palette bridge.
 *
 * MUI cannot read the CSS custom properties in src/styles/base/_tokens.scss:
 * createTheme() runs lighten()/darken() over palette.*.main to derive hover and
 * focus states, and that color math cannot parse `var(--c-primary)`. So the
 * accent is mirrored here as literal hex and kept in sync with _tokens.scss by
 * hand - these are the only hardcoded colors left in the app.
 *
 * Section awareness comes from the route instead: this provider sits inside
 * <BrowserRouter> (src/index.js), so useLocation() lets the theme swap accents
 * on navigation, matching what the .admin / .dashboard CSS scopes do for
 * Tailwind. Without this, every MUI input would render in one fixed color while
 * the surrounding UI changed per section.
 *
 * Contrast: white on each `main` is >= 5.3:1, so contrastText is safe on all three.
 */
const SECTION_ACCENTS = {
    landing: { main: "#0056FF", dark: "#013EB6", light: "#E8EFFF" }, // 5.56:1
    teacher: { main: "#0E7490", dark: "#0A5468", light: "#E2F2F7" }, // 5.36:1
    student: { main: "#7C3AED", dark: "#5B21B6", light: "#EEE9FE" }, // 5.70:1
};

// Shared spine - identical in all three sections, mirrors _tokens.scss.
const SUCCESS = "#11702A"; // 6.22:1
const WARNING = "#B45309"; // 5.02:1
const DANGER = "#D92D20"; // 4.83:1
const INK = "#2C3146"; // 12.85:1
const INK_MUTED = "#4A5468"; // 7.61:1
const RULE_SUBTLE = "#EAEBED";

const MuiRTLThemeProvider = ({ children }) => {
    const { pathname } = useLocation();
    const section = sectionFor(pathname);

    const theme = React.useMemo(() => {
        const accent = SECTION_ACCENTS[section];

        return createTheme({
            direction: "rtl",
            typography: {
                fontFamily: `"Sans-Web", "Sans-Mobile", "sans-serif"`,
                fontSize: 20,
            },
            components: {
                MuiTextField: {
                    styleOverrides: {
                        root: {
                            "& label": {
                                left: "unset",
                                right: "2.8rem",
                                transformOrigin: "right",
                            },
                            "& legend": {
                                textAlign: "right",
                                // fontSize: "0.6rem",
                            },
                        },
                    },
                },
            },
            palette: {
                primary: { ...accent, contrastText: "#FFFFFF" },
                // Only used by two InfoIcons; kept green so they do not shift.
                secondary: { main: SUCCESS, contrastText: "#FFFFFF" },
                success: { main: SUCCESS, contrastText: "#FFFFFF" },
                warning: { main: WARNING, contrastText: "#FFFFFF" },
                error: { main: DANGER, contrastText: "#FFFFFF" },
                info: { main: accent.main, contrastText: "#FFFFFF" },
                text: {
                    primary: INK,
                    secondary: INK_MUTED,
                },
                divider: RULE_SUBTLE,
            },
        });
    }, [section]);

    return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export default MuiRTLThemeProvider;
